import { useEffect, useState } from "react";
import { SimpleGrid, Card, Stack, Text, Badge, Group, Loader, Center, Box } from "@mantine/core";
import { IconArmchair2, IconClock, IconToolsKitchen2, IconCash, IconBell, IconUser, IconCalendarTime } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { EmptyState } from "../../../shared/ui";
import { useTables, useReservations } from "../../../shared/api";
import { useWebSocket } from "../../../shared/ws";
import { useAuth } from "../../../shared/auth/AuthProvider";

const STATE_COLOR: Record<string, string> = {
  FREE: "green",
  OCCUPIED: "red",
  RESERVED: "yellow",
  CLEANING: "gray",
  OUT_OF_SERVICE: "dark",
};

// QR'dan hesap isteyen masa: state ne olursa olsun mor ile öne çıkar.
const PAYMENT_REQUESTED_COLOR = "grape";

const STATE_LABEL: Record<string, string> = {
  FREE: "Boş",
  OCCUPIED: "Dolu",
  RESERVED: "Rezerve",
  CLEANING: "Temizlik",
  OUT_OF_SERVICE: "Servis Dışı",
};

/**
 * Bir zaman damgasından bu ana kadar geçen süreyi kısa biçimde döndürür.
 * "az önce" / "12 dk" / "1 sa 20 dk" — masa kartı köşe etiketleri için.
 */
function elapsedShort(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return "az önce";
  const min = Math.floor(ms / 60000);
  if (min < 1) return "az önce";
  if (min < 60) return `${min} dk`;
  const h = Math.floor(min / 60);
  return `${h} sa ${min % 60} dk`;
}

/** Geçen süreye göre renk — uzun bekleyen masa dikkat çeksin. */
function elapsedColor(iso: string | null | undefined): string {
  if (!iso) return "gray";
  const min = (Date.now() - new Date(iso).getTime()) / 60000;
  if (min >= 45) return "red";
  if (min >= 20) return "orange";
  return "gray";
}

/**
 * "Yeni" rozetleri için yanıp sönme animasyonu — garson onay bekleyen QR
 * siparişini kaçırmasın. CSS keyframe'i global olarak bir kez enjekte edilir.
 */
const BLINK_KEYFRAMES = `
@keyframes pos-blink {
  0%, 100% { opacity: 1; transform: scale(1); }
  50%      { opacity: 0.35; transform: scale(0.88); }
}
/* Filtre chip satırı yatay kayar ama scroll bar görünmez. */
.pos-filter-scroll { scrollbar-width: none; -ms-overflow-style: none; }
.pos-filter-scroll::-webkit-scrollbar { display: none; }
`;

export function TablesPage({ onSelectTable }: { onSelectTable: (id: string) => void }) {
  const qc = useQueryClient();

  // WS bağlıyken polling kapalı, koparsa 15 sn fallback (useTables içinde).
  const { lastMessage, status: wsStatus } = useWebSocket<any>("/ws/waiters/");
  const { data, isLoading } = useTables({ wsConnected: wsStatus === "OPEN" });
  const allTables = data?.results || data || [];

  // Onaylı rezervasyonları masaya eşle — masa kartında "şu saatte rezerve,
  // misafir X" göstermek için. Salon Planı (admin) ile aynı yaklaşım:
  // yalnızca BUGÜN başlayacak ve henüz geçmemiş rezervasyon gösterilir;
  // ileri tarihli rezervasyon canlı masa listesinde rozet basmamalı. Aynı
  // masada birden çok bugünkü rezervasyon varsa en erken başlayan seçilir.
  const { data: reservData } = useReservations({ status: "CONFIRMED" });
  const reservMap = new Map<string, any>();
  const _now = new Date();
  const _todayStr = _now.toDateString();
  ((reservData?.results || reservData || []) as any[])
    .filter((r) => {
      if (!r.starts_at) return false;
      const start = new Date(r.starts_at);
      return start.toDateString() === _todayStr && start >= _now;
    })
    .sort((a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime())
    .forEach((r) => {
      if (!reservMap.has(r.table)) reservMap.set(r.table, r);
    });

  // "Masalarım" toggle — açıkken sadece giriş yapan garsona atanmış masalar.
  // Durum filtresi (chip) + sağ üst rozet sayıları hepsi bu süzülmüş liste
  // üzerinde çalışır, böylece garson yalnız kendi masalarındaki sipariş/hazır/
  // hesap durumunu görür.
  const { user } = useAuth();
  const currentUserId = user?.id || null;
  const [myTablesOnly, setMyTablesOnly] = useState(false);
  const tables = myTablesOnly && currentUserId
    ? allTables.filter((t: any) => t.assigned_waiter === currentUserId)
    : allTables;

  // Masa planı durum filtresi — "Tümü" varsayılan.
  const [tableFilter, setTableFilter] = useState<string>("all");

  // Süre etiketlerinin canlı akması için her 30 sn'de bir yeniden render
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, []);

  // Masa durumu / sipariş push'ları — anlık tazele.
  //
  // payment.completed eklenmesi ÖNEMLİ: ödeme tamamlanıp masa CLEANING'e
  // alındığında backend hem 'payment.completed' hem 'table.state_changed'
  // yayınlar. Defensive olarak ikisini de dinliyoruz — eğer
  // _release_table_if_idle bir nedenle (try/except sessiz hata, tek
  // siparişli masa değil vs.) state_changed yayınlayamasa bile UI
  // taze veriyi çeker. Tek satırlık ek invalidate (tables endpoint hafif),
  // 'manuel refresh ile düzeliyor' bug'ını ortadan kaldırır.
  // kds.ticket.ready toast'ı App.tsx (global) seviyesinde gösterilir +
  // tables cache invalidate edilir; burada yalnız diğer event'lerle
  // ilgilenmek yeterli.
  useEffect(() => {
    if (!lastMessage) return;
    const ev = lastMessage.event;
    if (
      ev === "table.state_changed" ||
      ev === "order.confirmed" ||
      ev === "order.status_changed" ||  // READY/PREPARING → "HAZIR" rozeti/filtresi
      ev === "kds.ticket.ready" ||      // mutfak hazır → masa kartı HAZIR yansısın
      ev === "kds.ticket.served" ||     // servis edildi → HAZIR rozeti düşsün
      ev === "payment.completed" ||
      ev === "qr.order.pending_approval" ||
      ev === "qr.request.pending_approval" ||  // yeni mimari QR siparişi
      ev === "qr.request.cancelled" ||         // iptal → rozet/filtre düşsün
      ev === "qr.payment.requested" ||
      ev === "qr.payment.call_waiter"
    ) {
      qc.invalidateQueries({ queryKey: ["tables"] });
    }
  }, [lastMessage, qc]);

  if (isLoading) {
    return (
      <Center h={300}>
        <Loader />
      </Center>
    );
  }

  if (allTables.length === 0) {
    return <EmptyState title="Henüz masa tanımlanmamış" description="Yöneticinizden masa eklemesini isteyin." />;
  }

  // Sağ üst köşe sayaçları: QR'dan hesap isteyen masa adedi +
  // garson onayı bekleyen toplam yeni QR sipariş adedi + mutfakta hazır adet.
  // NOT: Bu sayaçlar HER ZAMAN tüm masalardan (allTables) hesaplanır —
  // "Masalarım" toggle'ından bağımsız. Aksi halde atanmış masası olmayan
  // garson toggle'ı açınca tüm sayılar 0'a düşer ve "refresh'siz düzelmiyor"
  // algısı oluşurdu. Görünür liste (visibleTables) ayrıca filtrelenir.
  const paymentRequestedCount = allTables.filter((t: any) => t.payment_requested).length;
  const newOrderCount = allTables.reduce(
    (sum: number, t: any) => sum + (t.qr_pending_count || 0),
    0,
  );
  const readyTotalCount = allTables.reduce(
    (sum: number, t: any) => sum + (t.ready_order_count || 0),
    0,
  );

  // Masa planı filtresi — garson kalabalık salonda hızlı süzsün.
  const _state = (t: any) => t.state || t.current_state || "FREE";
  const FILTERS: {
    key: string; label: string; color: string;
    match: (t: any) => boolean;
  }[] = [
    { key: "all", label: "Tümü", color: "blue", match: () => true },
    {
      key: "order", label: "Sipariş", color: "orange",
      match: (t) => (t.qr_pending_count || 0) > 0,
    },
    {
      // Hazır: koyu yeşil — "Boş"un açık yeşilinden net ayrılsın.
      key: "ready", label: "Hazır", color: "teal.9",
      match: (t) => (t.ready_order_count || 0) > 0,
    },
    {
      key: "payment", label: "Hesap", color: PAYMENT_REQUESTED_COLOR,
      match: (t) => !!t.payment_requested,
    },
    // Boş: açık yeşil — "Hazır"ın koyu yeşilinden net ayrılsın.
    { key: "free", label: "Boş", color: "green.5", match: (t) => _state(t) === "FREE" },
    { key: "occupied", label: "Dolu", color: "red", match: (t) => _state(t) === "OCCUPIED" },
  ];
  const activeFilter = FILTERS.find((f) => f.key === tableFilter) ?? FILTERS[0];
  // Görünür liste = "Masalarım" süzgeci (tables) + seçili durum filtresi.
  const visibleTables = tables.filter(activeFilter.match);

  return (
    <Stack gap="md">
      <style>{BLINK_KEYFRAMES}</style>
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        {/* Başlık büyük; alt yazı ALTINDA — sağ üstteki yanıp sönen
            rozetlere (yeni sipariş / hesap istiyor) yer kalsın diye dikey. */}
        <Stack gap={2} style={{ minWidth: 0 }}>
          <Text fw={700} size="28px" lh={1.1} style={{ whiteSpace: "nowrap" }}>
            Masa Planı
          </Text>
          <Text size="sm" c="dimmed" lineClamp={1}>
            Masaya basarak sipariş açın
          </Text>
        </Stack>
        <Stack gap={6} align="flex-end">
          {paymentRequestedCount > 0 && (
            <Badge
              size="lg"
              variant="filled"
              color={PAYMENT_REQUESTED_COLOR}
              leftSection={<IconCash size={14} />}
              style={{ animation: "pos-blink 1.2s ease-in-out infinite" }}
            >
              {paymentRequestedCount} masa hesap istiyor
            </Badge>
          )}
          {newOrderCount > 0 && (
            <Badge
              size="lg"
              variant="filled"
              color="orange"
              leftSection={<IconBell size={14} />}
              style={{ animation: "pos-blink 1.2s ease-in-out infinite" }}
            >
              {newOrderCount} yeni sipariş onay
            </Badge>
          )}
          {readyTotalCount > 0 && (
            <Badge
              size="lg"
              variant="filled"
              color="teal.9"
              leftSection={<IconToolsKitchen2 size={14} />}
              style={{ animation: "pos-blink 1.2s ease-in-out infinite" }}
            >
              {readyTotalCount} sipariş hazır
            </Badge>
          )}
        </Stack>
      </Group>

      {/* Durum filtresi — TEK satır; dar ekranda alta kaymaz, yatay kayar.
          STICKY: sayfa kaydırılırken üstte sabit kalır, garson filtreyi
          kaybetmez. Beyaz zemin + zIndex ile altındaki kartların üstünde durur. */}
      <Group
        gap={6}
        wrap="nowrap"
        className="pos-filter-scroll"
        style={{
          overflowX: "auto",
          position: "sticky",
          // AppShell.Header 52px — sticky onun ALTINA yapışsın, arkasında
          // kalıp görünmez olmasın. (App.tsx header={{ height: 52 }})
          top: 52,
          zIndex: 100,
          background: "var(--mantine-color-body)",
          // Üstte/altta nefes boşluğu — chip'ler kartlara yapışık durmasın.
          paddingTop: 12,
          paddingBottom: 12,
          marginBottom: 4,
        }}
      >
        {/* "Masalarım" toggle — durum chip'lerinden ayrı bir anahtar. Açıkken
            sadece garsona atanmış masalar; durum chip'leri onun üzerinde süzer. */}
        {currentUserId && (
          <Badge
            size="lg"
            color="indigo"
            variant={myTablesOnly ? "filled" : "outline"}
            leftSection={<IconUser size={13} />}
            style={{ cursor: "pointer", userSelect: "none", flexShrink: 0 }}
            onClick={() => setMyTablesOnly((v) => !v)}
          >
            Masalarım{" "}
            {allTables.filter((t: any) => t.assigned_waiter === currentUserId).length}
          </Badge>
        )}
        {FILTERS.map((f) => {
          // Durum sayıları "Masalarım"dan BAĞIMSIZ — her zaman tüm masalardan
          // (allTables) sayılır. Atanmış masası olmayan garson toggle'ı açsa
          // bile TÜMÜ/SİPARİŞ/HAZIR gerçek toplamı gösterir, 0'a düşmez.
          const count = allTables.filter(f.match).length;
          const selected = f.key === tableFilter;
          return (
            <Badge
              key={f.key}
              size="lg"
              color={f.color}
              variant={selected ? "filled" : "light"}
              style={{
                cursor: "pointer", userSelect: "none", flexShrink: 0,
              }}
              onClick={() => {
                setTableFilter(f.key);
                // Durum chip'ine basmak net bir "tüm masalara dön" jesti —
                // "Masalarım" açık + 0 atanmış masada kilitlenmeyi önler.
                setMyTablesOnly(false);
              }}
            >
              {f.label} {count}
            </Badge>
          );
        })}
      </Group>

      <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="md">
        {visibleTables.length === 0 ? (
          <Box style={{ gridColumn: "1 / -1" }}>
            <Text c="dimmed" ta="center" py="xl">
              Bu filtreye uyan masa yok.
            </Text>
          </Box>
        ) : visibleTables.map((t: any) => {
          const state = t.state || t.current_state || "FREE";
          const isOccupied = state === "OCCUPIED";
          // Bu masaya bağlı onaylı rezervasyon (varsa) — kartta saat + misafir.
          const reservation = reservMap.get(t.id);
          // QR'dan hesap istendiyse masa moru ile öne çıkar (state'i ezer).
          const paymentRequested = !!t.payment_requested;
          // Garson onayı bekleyen QR sipariş sayısı (DRAFT + PendingQrRequest)
          const qrPending = t.qr_pending_count || 0;
          // Mutfakta hazırlanması biten — garson "git al" rozeti.
          const readyCount = t.ready_order_count || 0;
          // Açık süresi: masa OCCUPIED'a geçtiğinden beri (state_changed_at)
          const openFor = isOccupied ? elapsedShort(t.state_changed_at) : null;
          // Son siparişten geçen süre
          const sinceOrder = isOccupied ? elapsedShort(t.last_order_at) : null;

          // Hesap isteyen masa moru ile çerçevelenir + hafif mor zemin alır.
          const borderColor = paymentRequested
            ? `var(--mantine-color-${PAYMENT_REQUESTED_COLOR}-6)`
            : `var(--mantine-color-${STATE_COLOR[state]}-6)`;

          return (
            <Card
              key={t.id}
              padding="lg"
              radius="md"
              withBorder
              onClick={() => onSelectTable(t.id)}
              style={{
                cursor: "pointer",
                borderColor,
                borderWidth: 2,
                minHeight: 140,
                position: "relative",
                backgroundColor: paymentRequested
                  ? "var(--mantine-color-grape-0)"
                  : undefined,
              }}
            >
              {/* Sol üst köşe — açık sipariş sayısı rozeti */}
              {t.open_order_count > 0 && (
                <Badge
                  size="sm"
                  variant="filled"
                  color="blue"
                  style={{ position: "absolute", top: 8, left: 8 }}
                >
                  {t.open_order_count} sipariş
                </Badge>
              )}

              {/* Sol üst köşe (sipariş rozetinin altı) — garson onayı
                  bekleyen QR sipariş sayısı. "yeni" sticker'ı gibi yanıp
                  söner, garson kaçırmasın. */}
              {qrPending > 0 && (
                <Badge
                  size="sm"
                  variant="filled"
                  color="orange"
                  leftSection={<IconBell size={11} />}
                  style={{
                    position: "absolute",
                    top: t.open_order_count > 0 ? 34 : 8,
                    left: 8,
                    animation: "pos-blink 1.2s ease-in-out infinite",
                  }}
                  title={`${qrPending} QR siparişi onay bekliyor`}
                >
                  {qrPending} yeni
                </Badge>
              )}

              {/* Sol üst — mutfakta hazır sipariş. Yanıp söner, garson "git
                  al" için kaçırmasın. qrPending rozeti varsa onun altına biner. */}
              {readyCount > 0 && (
                <Badge
                  size="sm"
                  variant="filled"
                  color="teal.9"
                  leftSection={<IconToolsKitchen2 size={11} />}
                  style={{
                    position: "absolute",
                    top: (t.open_order_count > 0 ? 34 : 8) + (qrPending > 0 ? 26 : 0),
                    left: 8,
                    animation: "pos-blink 1.2s ease-in-out infinite",
                  }}
                  title={`${readyCount} sipariş mutfakta hazır`}
                >
                  {readyCount} HAZIR
                </Badge>
              )}

              {/* Sağ üst köşe — masa kaç dk'dır açık */}
              {openFor && (
                <Group
                  gap={3}
                  wrap="nowrap"
                  style={{ position: "absolute", top: 8, right: 8 }}
                >
                  <IconClock size={12} color={`var(--mantine-color-${elapsedColor(t.state_changed_at)}-6)`} />
                  <Text size="10px" fw={600} c={elapsedColor(t.state_changed_at)}>
                    {openFor}
                  </Text>
                </Group>
              )}

              <Stack gap="xs" align="center">
                <IconArmchair2 size={32} />
                <Text fw={700} size="xl">
                  {t.name}
                </Text>
                <Badge color={STATE_COLOR[state]} size="lg">
                  {STATE_LABEL[state]}
                </Badge>
                {paymentRequested && (
                  <Badge
                    color={PAYMENT_REQUESTED_COLOR}
                    size="lg"
                    variant="filled"
                    leftSection={<IconCash size={14} />}
                  >
                    Hesap İstiyor
                  </Badge>
                )}
                <Text size="xs" c="dimmed">
                  {t.capacity} kişi
                </Text>

                {/* Rezervasyon bilgisi — masa bu saatte rezerve, hangi
                    misafire. Garson "bu masa rezervasyondan geldi/gelecek"
                    olduğunu kart üzerinden görür. */}
                {reservation && (
                  <Badge
                    color="yellow"
                    variant="filled"
                    size="md"
                    leftSection={<IconCalendarTime size={13} />}
                  >
                    Rezerve {new Date(reservation.starts_at).toLocaleTimeString("tr-TR", {
                      hour: "2-digit", minute: "2-digit",
                    })}
                    {(reservation.customer_name || reservation.customer_phone)
                      ? ` · ${reservation.customer_name || reservation.customer_phone}`
                      : ""}
                  </Badge>
                )}
              </Stack>

              {/* Alt sağ — son siparişten geçen süre */}
              {sinceOrder && (
                <Box style={{ position: "absolute", bottom: 8, right: 8 }}>
                  <Group gap={3} wrap="nowrap">
                    <IconToolsKitchen2 size={11} color="var(--mantine-color-gray-6)" />
                    <Text size="9px" c="dimmed">
                      son sipariş {sinceOrder} önce
                    </Text>
                  </Group>
                </Box>
              )}
            </Card>
          );
        })}
      </SimpleGrid>
    </Stack>
  );
}
